import { emptyApiSlice } from '../emptyApiSlice';
import {
  ExportConfig,
  ExportConfigLogoRaw,
  ExportConfigRaw,
  SaveExportConfigRequest,
} from './pdfReportApiTypes';

const EXPORT_CONFIG_DATA_TYPE =
  'Xovi\\API\\Data\\Types\\XoviAI\\Export\\Config';
const LOGO_DATA_TYPE = 'Xovi\\API\\Data\\Types\\Logo';
const MEDIA_DATA_TYPE = 'Xovi\\API\\Data\\Types\\DataURL';
const ITEM_LIST_DATA_TYPE =
  'Xovi\\API\\Data\\Types\\XoviAI\\Export\\Content\\ItemList';
const ITEM_DATA_TYPE = 'Xovi\\API\\Data\\Types\\XoviAI\\Export\\Content\\Item';

const fromRaw = (raw: ExportConfigRaw): ExportConfig => {
  const { data } = raw;
  const rawLogo = data.logo?.data;
  const rawMedia = rawLogo?.media?.data;
  return {
    id: data.id,
    logo: rawLogo
      ? {
          id: rawLogo.id,
          name: rawLogo.name,
          mediaType: rawMedia?.['media-type'],
          mediaData: rawMedia?.['media-data'],
        }
      : null,
    name: data.name,
    items: (data.items?.data ?? []).map(item => ({ ...item.data })),
  };
};

const toRawLogo = (
  logo: NonNullable<SaveExportConfigRequest['logo']>
): ExportConfigLogoRaw => {
  if (logo.id && !logo.mediaData) {
    return { 'data-type': LOGO_DATA_TYPE, data: { id: logo.id } };
  }
  return {
    'data-type': LOGO_DATA_TYPE,
    data: {
      name: logo.name,
      media: {
        'data-type': MEDIA_DATA_TYPE,
        data: {
          'media-type': logo.mediaType ?? '',
          'media-encoding': 'base64',
          'media-data': logo.mediaData ?? '',
        },
      },
    },
  };
};

const toRaw = (
  config: Pick<SaveExportConfigRequest, 'logo' | 'name' | 'items'>
): ExportConfigRaw => ({
  'data-type': EXPORT_CONFIG_DATA_TYPE,
  data: {
    ...(config.logo && { logo: toRawLogo(config.logo) }),
    ...(config.name !== undefined && { name: config.name }),
    ...(config.items && {
      items: {
        'data-type': ITEM_LIST_DATA_TYPE,
        data: config.items.map(item => ({
          'data-type': ITEM_DATA_TYPE,
          data: { id: item.id, enabled: item.enabled },
        })),
      },
    }),
  },
});

export const pdfReportApiSlice = emptyApiSlice.injectEndpoints({
  endpoints: builder => ({
    getExportConfig: builder.query<ExportConfig, string>({
      query: domainId => ({
        url: `domains/${domainId}/export/config`,
        method: 'GET',
      }),
      transformResponse: fromRaw,
      providesTags: ['ExportConfig'],
    }),
    saveExportConfig: builder.mutation<ExportConfig, SaveExportConfigRequest>({
      query: ({ domainId, ...config }) => ({
        url: `domains/${domainId}/export/config`,
        method: 'PUT',
        body: toRaw(config),
      }),
      transformResponse: fromRaw,
      invalidatesTags: ['ExportConfig'],
    }),
  }),
});

export const { useGetExportConfigQuery, useSaveExportConfigMutation } =
  pdfReportApiSlice;
